import React, { useCallback, useEffect, useState } from 'react'
import { Button, PageSection, Title } from '@patternfly/react-core'
import { TableComposable, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table'
import { Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'design-react-kit'
import GenericSelect from 'src/frontend/app/components/Form/Select'
import { generateRequestFilter, generateRequestSort } from 'src/app/utils/ApiUtils'
import { RO_GEST_FORM_ODAF } from 'src/app/utils/RoutesConstants'
import { getDefaultAxiosConfig, getFormazioneResourceApi } from 'src/app/utils/ManagerRestGateway'
import GenericBreadCrumb from 'src/frontend/app/components/GenericBreadCrumb'
import { TokenStore } from 'src/keycloak/jwt/TokenStore'
import useFrontOffice from 'src/hooks/useFrontOffice'
import GenericInput from 'src/frontend/app/components/Form/Input'
import moment from 'moment'
import { exportPDF, ottieniAllegato } from 'src/frontend/app/pages/iscrizioneAlboEF/form-utils'
import { TRIENNI_OPTIONS } from './CreaProroga'
import { GenericSpinner } from '../../components'
import { getSoggettoByCf } from 'src/processes/Soggetto'
import { RiepilogoCreditiFormativiView } from '@parsec/msfo'

const myPath = {
  label: 'Elenco Crediti Formativi',
}

export const annoToTriennio = (anno: number) => {
  if (anno === 2013) {
    return 671
  } else if (anno < 2013) {
    return Math.floor(anno / 3)
  } else {
    return Math.floor((anno - 1) / 3)
  }
}

export const getAnnoAperturaByTriennio = (triennio: number) => {
  if (triennio <= 671) {
    return triennio * 3
  } else {
    return triennio * 3 + 1
  }
}

export const getAnnoChiusuraByTriennio = (triennio: number) => {
  if (triennio < 671) {
    return triennio * 3 + 2
  } else {
    return triennio * 3 + 1 + 2
  }
}

const triennioToString = (triennio: number) => {
  const apertura = getAnnoAperturaByTriennio(triennio)
  const chiusura = getAnnoChiusuraByTriennio(triennio)
  return apertura + ' - ' + chiusura
}

const Spacer = () => <br></br>

const basePath = { label: 'Formazione' }

const CreditiFormativi: React.FunctionComponent = () => {
  const searchParams = new URLSearchParams(window.location.search)
  const cf = searchParams.get('cf')
  const flagApprovazione = searchParams.get('flagApprovazione')
  const isFrontOffice = useFrontOffice()

  const [codFisc, setCodFisc] = useState<string>(!isFrontOffice && cf ? cf : '')
  const cfUser = TokenStore.getInstance().getSpidData().codiceFiscale
  const [dataIscrizione, setDataIscrizione] = useState<any>(null)
  const [esoneri, setEsoneri] = useState<any>([])
  const [datiPerTriennio, setDatiPerTriennio] = useState<any>({})
  const [infoPerTriennio, setInfoPerTriennio] = useState<any>({})
  const [triennioSelezionato, setTriennioSelezionato] = useState<Option>({ label: triennioToString(675), value: 675 })
  const [approvaCliccato, setApprovaCliccato] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [metaprofPerAnno, setMetaprofPerAnno] = useState<{ [key: number]: number }>({})
  const [approvazioni, setApprovazioni] = useState<Record<string, any>>({})
  const approvazioneCorrente = approvazioni?.[String(triennioSelezionato.value)]
  const effectiveCf = isFrontOffice ? cfUser : codFisc

  const [dataApprovazione, setDataApprovazione] = useState<string>('')

  const buildLocalDateTime = (date: string) => {
    if (!date) return null
    return `${date}T00:00:00`
  }

  const [showModal, setShowModal] = useState(false)

  const handleInput = (id_data_number, dato: string) => {
    setCodFisc(dato)

    setDatiPerTriennio({})
    setInfoPerTriennio({})
    setTriennioSelezionato({ label: triennioToString(675), value: 675 })

    setApprovazioni({})
  }

  useEffect(() => {
    if (flagApprovazione) {
      setTriennioSelezionato((prevTriennio) => ({
        label: triennioToString(prevTriennio.value - 1),
        value: prevTriennio.value - 1,
      }))
    }
  }, [flagApprovazione])

  const loadApprovazioni = useCallback(async () => {
    const effectiveCf = isFrontOffice ? cfUser : codFisc

    if (effectiveCf?.length !== 16) return

    const res = isFrontOffice
      ? await getFormazioneResourceApi().msfoFormazioneListaTriennioFormativoForUserGet(
          generateRequestFilter([]),
          -1,
          -1,
          generateRequestSort('triennio', 'desc'),
          getDefaultAxiosConfig(),
        )
      : await getFormazioneResourceApi().msfoFormazioneListaTriennioFormativoGet(
          effectiveCf,
          generateRequestFilter([]),
          -1,
          -1,
          generateRequestSort('triennio', 'desc'),
          getDefaultAxiosConfig(),
        )

    const approvazioniMap = (res.data.items || []).reduce((acc, curr) => {
      acc[curr.triennio] = curr
      return acc
    }, {})

    setApprovazioni((prev) => {
      const same = JSON.stringify(prev) === JSON.stringify(approvazioniMap)

      if (same) return prev

      return approvazioniMap
    })
  }, [codFisc, cfUser, isFrontOffice])

  useEffect(() => {
    loadApprovazioni()
  }, [loadApprovazioni])

  const initData = useCallback(async () => {
    if (!triennioSelezionato?.value) return
    if (effectiveCf?.length !== 16) return

    setLoading(true)

    try {
      let dataIscrizioneTmp = null

      if (!isFrontOffice && codFisc) {
        try {
          const iscrizioneResponse = await getFormazioneResourceApi().msfoFormazioneDataIscrizioneByCfGet(codFisc, getDefaultAxiosConfig())

          dataIscrizioneTmp = iscrizioneResponse.data.dataIscrizione

          setDataIscrizione(dataIscrizioneTmp)
          setEsoneri(iscrizioneResponse.data.richiestaEsoneroList || [])
        } catch (e) {
          console.error('Errore recupero iscrizione', e)
        }
      }

      const response = isFrontOffice
        ? await getFormazioneResourceApi().msfoFormazioneRiepilogoCreditiFormativiForUserGet(triennioSelezionato.value, getDefaultAxiosConfig())
        : await getFormazioneResourceApi().msfoFormazioneRiepilogoCreditiFormativiGet(codFisc, triennioSelezionato.value, getDefaultAxiosConfig())

      const data: RiepilogoCreditiFormativiView[] = response.data || []
      const grouped: Record<number, RiepilogoCreditiFormativiView[]> = {}

      const info: Record<
        number,
        {
          cfp_validi: number
          cfp_metaprofessionali: number
        }
      > = {}

      const metaprofMap: Record<number, number> = {}

      for (const item of data) {
        if (!item.triennio) {
          continue
        }

        if (!grouped[item.triennio]) {
          grouped[item.triennio] = []
        }

        grouped[item.triennio].push(item)

        if (!info[item.triennio]) {
          info[item.triennio] = {
            cfp_validi: 0,
            cfp_metaprofessionali: 0,
          }
        }

        const validi = item.cfpValidi || 0

        info[item.triennio].cfp_validi += validi

        if (item.tipoCfp === 'AFM') {
          info[item.triennio].cfp_metaprofessionali += validi

          if (item.annoCalcCfp) {
            if (!metaprofMap[item.annoCalcCfp]) {
              metaprofMap[item.annoCalcCfp] = 0
            }

            metaprofMap[item.annoCalcCfp] += validi
          }
        }
      }

      setDatiPerTriennio(grouped)

      setInfoPerTriennio(info)

      setMetaprofPerAnno(metaprofMap)

      setApprovaCliccato(false)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }, [effectiveCf, triennioSelezionato.value])

  const approvaTriennio = useCallback(async () => {
    const triennio = triennioSelezionato.value
    const eventiFormativi = datiPerTriennio[triennio]
    const info = infoPerTriennio[triennio]
    const caratterizzanti = info.cfp_validi - info.cfp_metaprofessionali
    let htmlCrediti = `
      <p class="italicheader center">Regolare</p>
      <p class="italicheader center">Per il triennio formativo ${triennioToString(triennio)}</p>
      <p class="spaced">Crediti Maturati</p>
      <p class="spaced">CFP Caratterizzanti n° ${Math.round(caratterizzanti * 1000) / 1000}</p>
      <p class="spaced">CFP Metaprofessionali n° ${Math.round(info.cfp_metaprofessionali * 1000) / 1000}</p>
    `
    let htmlEventi = `
      <p class="spaced">Elenco attività formative <span class="italicheader">triennio formativo ${triennioToString(triennio)}</span></p>
    `
    for (const evento of eventiFormativi) {
      htmlEventi += `
        <p class="spaced">${evento.titoloEvento}</p>
      `
    }

    const object = {
      cf: codFisc,
      triennio: triennioSelezionato.value,
      approvato: 1,
      testoCrediti: htmlCrediti,
      testoEventi: htmlEventi,
      dataApprovazione: buildLocalDateTime(dataApprovazione),
    }

    setLoading(true)

    await getFormazioneResourceApi().msfoFormazioneApprovaTriennioFormativoPost(object, getDefaultAxiosConfig())

    await loadApprovazioni()

    setLoading(false)

    initData()
  }, [codFisc, triennioSelezionato, datiPerTriennio, infoPerTriennio, initData, dataApprovazione])

  const confermaApprovaTriennio = () => {
    setApprovaCliccato(true)
    setShowModal(false)
    approvaTriennio()
  }

  const closeModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    initData()
  }, [effectiveCf, triennioSelezionato.value])

  const getColumnValue = (column: any, row: any) => {
    if (column.field && row[column.field] !== undefined) {
      const value = row[column.field]
      if (column.options?.[value]) {
        return column.options[value]
      }
      return row[column.field]
    }
    return ''
  }

  const columns: Column[] = [
    { label: 'SDAF', field: 'settore', isSortable: false, width: 100 },
    { label: 'Tipologie Attività', field: 'tipologiaAttivita', isSortable: false, width: 80 },
    {
      label: 'Data inizio',
      field: 'dataInizio',
      isSortable: false,
      width: 200,
      render: (row) => (row.dataInizio ? moment(row.dataInizio).format('DD-MM-YYYY') : ''),
    },
    { label: 'Data fine', field: 'dataFine', isSortable: false, width: 200, render: (row) => (row.dataFine ? moment(row.dataFine).format('DD-MM-YYYY') : '') },
    { label: 'Titolo Evento', field: 'titoloEvento', isSortable: false, width: 800 },
    {
      label: 'Anno',
      field: 'annoCalcCfp',
      isSortable: false,
      render: (row) => (row.isRiconciliato && row.dbEvento === 'R' ? 'RIPARTITO (' + row.annoCalcCfp + ')' : row.annoCalcCfp),
    },
    { label: 'Tipo CFP', field: 'tipoCfp', isSortable: false, width: 80 },
    {
      label: 'CFP Riconosciuti',
      field: 'cfpRiconosciuti',
      isSortable: false,
      width: 80,
      render: (row) => (row.cfpRiconosciuti == null ? '' : Number.parseFloat(Number(row.cfpRiconosciuti).toFixed(3))),
    },
    {
      label: 'CFP validi per gli obblighi formativi',
      field: 'cfpValidi',
      isSortable: false,
      width: 150,
      render: (row) => (row.cfpValidi == null ? '' : Number.parseFloat(Number(row.cfpValidi).toFixed(3))),
    },
    { label: 'Organizzatore', field: 'organizzatore', isSortable: false },
    {
      label: 'Attestato',
      field: 'attestato',
      isSortable: false,
      render: (row) => {
        if (
          (!row.attestatoPartecipazione && row?.dbEvento === 'E') ||
          (row?.dbEvento === 'R' && row.organizzatore !== 'NON A CATALOGO' && row.organizzatore !== 'INDIVIDUALE')
        ) {
          return (
            <Button
              variant="primary"
              onClick={async () => {
                setLoading(true)
                const res = await getFormazioneResourceApi().msfoFormazioneGeneraAttestatoPartecipazioneEventoPost(
                  { user_name: codFisc, ref: row?.idEvento } as any,
                  getDefaultAxiosConfig(),
                )
                ottieniAllegato(res.data, 'Attestato_partecipazione_evento')
                setLoading(false)
              }}
            >
              Scarica attestato
            </Button>
          )
        } else {
          return (
            <Button
              variant="primary"
              onClick={async () => {
                const res = await getFormazioneResourceApi().msfoFormazioneEventoNonACatalogoGet(row?.idEvento, getDefaultAxiosConfig())
                ottieniAllegato(res.data.attestatoDTO?.idAllegato)
              }}
            >
              Scarica attestato
            </Button>
          )
        }
      },
    },
  ]

  let minTotaleCFP = 9
  const minMetaTotaleTriennio = 1
  const minMetaEntroPrimoAnno = 0.5

  let validoMetaPrimoAnno = true

  if (triennioSelezionato && dataIscrizione) {
    const aperturaTriennio = getAnnoAperturaByTriennio(triennioSelezionato.value)
    const chiusuraTriennio = getAnnoChiusuraByTriennio(triennioSelezionato.value)

    const annoIscr = moment(dataIscrizione, 'YYYY-MM-DD').year()

    // ----------------------------
    // Calcolo anni validi per CFP totali
    // ----------------------------

    let anniValidi = 3

    if (annoIscr >= aperturaTriennio && annoIscr <= chiusuraTriennio) {
      anniValidi = chiusuraTriennio - annoIscr
    }

    minTotaleCFP = 3 * anniValidi

    // ----------------------------
    // Verifica 0.5 meta entro primo anno
    // (anno iscrizione + anno successivo)
    // ----------------------------
    let validoMetaPrimoAnno = true

    // Il controllo vale SOLO se la prima iscrizione cade nel triennio corrente
    if (annoIscr >= aperturaTriennio && annoIscr <= chiusuraTriennio) {
      const metaEntroPrimoAnno = (metaprofPerAnno[annoIscr] || 0) + (metaprofPerAnno[annoIscr + 1] || 0)

      validoMetaPrimoAnno = metaEntroPrimoAnno >= minMetaEntroPrimoAnno
    }

    // ----------------------------
    // Gestione esoneri (solo sui CFP totali)
    // ----------------------------

    const primoGiorno = moment(Math.max(annoIscr, aperturaTriennio) + '-01-01', 'YYYY-MM-DD')

    const ultimoGiorno = moment(chiusuraTriennio + '-12-31', 'YYYY-MM-DD')

    let giorniDiEsonero = 0
    const giorniTotali = ultimoGiorno.diff(primoGiorno, 'days')

    if (esoneri && giorniTotali > 0) {
      for (const esonero of esoneri) {
        let dataInizio = moment(esonero.data_inizio, 'YYYY-MM-DD')
        let dataFine = moment(esonero.data_fine, 'YYYY-MM-DD')

        if (dataFine.isBefore(primoGiorno) || dataInizio.isAfter(ultimoGiorno)) {
          continue
        }

        if (dataInizio.isBefore(primoGiorno)) {
          dataInizio = primoGiorno
        }

        if (dataFine.isAfter(ultimoGiorno)) {
          dataFine = ultimoGiorno
        }

        giorniDiEsonero += dataFine.diff(dataInizio, 'days')
      }

      const percentualeEsonero = giorniDiEsonero / giorniTotali
      const coefficienteRiduzione = 1 - percentualeEsonero

      minTotaleCFP = minTotaleCFP * coefficienteRiduzione
    }
  }

  // ---------------------------------
  // CONTROLLI FINALI
  // ---------------------------------

  let totaleCFP = 0
  let totaleMeta = 0

  if (infoPerTriennio[triennioSelezionato.value]) {
    totaleCFP = Math.round(infoPerTriennio[triennioSelezionato.value]?.cfp_validi * 1000) / 1000

    totaleMeta = Math.round(infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali * 1000) / 1000
  }

  const totaleCFPValido = totaleCFP >= minTotaleCFP
  const totaleMetaValido = totaleMeta >= minMetaTotaleTriennio

  const puoApprovare =
    totaleCFPValido &&
    totaleMetaValido &&
    validoMetaPrimoAnno &&
    !approvazioni[triennioSelezionato.value] &&
    !isFrontOffice &&
    TokenStore.getInstance().isCurrentAccountODAF() &&
    TokenStore.getInstance().currentAccountHasRolesOR([RO_GEST_FORM_ODAF])

  useEffect(() => {
    if (!approvaCliccato && puoApprovare && flagApprovazione) {
      setShowModal(true)
    }
  }, [approvaCliccato, puoApprovare, flagApprovazione])

  return (
    <PageSection>
      <div className="container register-container">
        {!isFrontOffice && (
          <>
            <GenericBreadCrumb paths={[basePath, myPath]} />
            <Spacer />
          </>
        )}

        <Title headingLevel="h1" size="lg">
          {myPath.label}
        </Title>
        {!isFrontOffice && (
          <GenericInput
            id="cf"
            type={'text'}
            placeholder={'CODICE FISCALE'}
            label={'Codice Fiscale*'}
            value={codFisc}
            onChange={handleInput}
            onKeyDown={() => {}}
          />
        )}

        {loading ? (
          <GenericSpinner />
        ) : (
          <>
            <GenericSelect
              id="selettoreTriennio"
              label="Seleziona il triennio scelto:"
              value={triennioSelezionato}
              defaultValue={675}
              defaultOptions={TRIENNI_OPTIONS}
              onChange={(code, value) => {
                setTriennioSelezionato(value)
                setApprovaCliccato(false)
              }}
            />
            <Spacer />
            {!approvaCliccato && puoApprovare && (
              // tolto ruolo per CONAF Nazionale vedi requisito pag. 153 solo ODAF può validare il triennio
              <>
                <Button variant="primary" onClick={() => setShowModal(true)} disabled={approvaCliccato}>
                  Approva Triennio
                </Button>

                <Modal isOpen={showModal} toggle={closeModal} centered>
                  <ModalHeader>Approvazione Triennio Formativo {triennioSelezionato.label}</ModalHeader>
                  <ModalBody>
                    <p>Sei sicuro di voler approvare il triennio per:</p>
                    <p>Iscritto: {codFisc}</p>
                    <p>CFP Totali Validi: {Math.round(infoPerTriennio[triennioSelezionato.value]?.cfp_validi * 1000) / 1000}</p>
                    <p>
                      CFP Caratterizzanti Validi:{' '}
                      {Math.round(
                        (infoPerTriennio[triennioSelezionato.value]?.cfp_validi - infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali) * 1000,
                      ) / 1000}
                    </p>
                    <p>CFP Metaprofessionali Validi: {Math.round(infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali * 1000) / 1000}</p>
                    {/* DATEPICKER OBBLIGATORIO */}
                    <div style={{ marginTop: 15 }}>
                      <label style={{ display: 'block', marginBottom: 5 }}>Data decorrenza certificato *</label>
                      <input
                        type="date"
                        value={dataApprovazione}
                        onChange={(e) => setDataApprovazione(e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </ModalBody>
                  <ModalFooter>
                    <Button color="secondary" onClick={closeModal} style={{ marginRight: '10px' }}>
                      Annulla
                    </Button>
                    <Button color="primary" onClick={confermaApprovaTriennio} isDisabled={!dataApprovazione}>
                      Conferma
                    </Button>
                  </ModalFooter>
                </Modal>
                <Spacer />
              </>
            )}
            {approvazioneCorrente && (
              <>
                <Button
                  variant="primary"
                  onClick={() => {
                    const idAllegato = approvazioneCorrente.idAttestato
                    if (idAllegato) ottieniAllegato(idAllegato)
                  }}
                >
                  Scarica attestato regolarità formativa
                </Button>
                <Spacer />
              </>
            )}
            {infoPerTriennio[triennioSelezionato.value] ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title headingLevel="h2" size="lg">
                  Totale CFP validi: {Math.round(infoPerTriennio[triennioSelezionato.value]?.cfp_validi * 1000) / 1000}. Di cui{' '}
                  {Math.round(infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali * 1000) / 1000} metaprofessionali
                </Title>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (codFisc) {
                      getSoggettoByCf(codFisc).then((response: any) => {
                        const { nome, cognome } = response.data[0]

                        exportPDF(
                          datiPerTriennio[triennioSelezionato.value],
                          nome,
                          cognome,
                          codFisc,
                          triennioSelezionato.label,
                          Math.round(infoPerTriennio[triennioSelezionato.value]?.cfp_validi * 1000) / 1000,
                          Math.round(infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali * 1000) / 1000,
                        )
                      })
                    } else {
                      const { codiceFiscale, nome, cognome } = TokenStore.getInstance().getSpidData()

                      exportPDF(
                        datiPerTriennio[triennioSelezionato.value],
                        nome,
                        cognome,
                        codiceFiscale,
                        triennioSelezionato.label,
                        Math.round(infoPerTriennio[triennioSelezionato.value]?.cfp_validi * 1000) / 1000,
                        Math.round(infoPerTriennio[triennioSelezionato.value].cfp_metaprofessionali * 1000) / 1000,
                      )
                    }
                  }}
                >
                  Scarica riepilogo formativo
                </Button>
              </div>
            ) : (
              <Title headingLevel="h2" size="lg">
                Totale CFP validi: 0. Di cui 0 metaprofessionali
              </Title>
            )}
            <PageSection>
              <TableComposable id="riepilogoCreditiFormativi" aria-label="Sort table" hidden={!infoPerTriennio[triennioSelezionato.value] && !loading}>
                <Thead>
                  <Tr>
                    {columns.map((column, index) => {
                      return (
                        <Th
                          key={`head${index}`}
                          id={column.field}
                          style={column.width ? { width: column.width, maxWidth: column.width, position: 'sticky' } : {}}
                        >
                          {column.label}
                        </Th>
                      )
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {datiPerTriennio[triennioSelezionato.value] && (
                    <>
                      {datiPerTriennio[triennioSelezionato.value].map((row, index) => {
                        return (
                          <Tr key={`row${index}`}>
                            {columns.map((column, index) => {
                              return (
                                <Td key={`col${index}`} style={column.style || {}}>
                                  {column.render?.(row)}
                                  {!column.render && getColumnValue(column, row)}
                                </Td>
                              )
                            })}
                          </Tr>
                        )
                      })}
                    </>
                  )}
                </Tbody>
              </TableComposable>
              {!infoPerTriennio[triennioSelezionato.value] && codFisc && loading && <Row>Nessuna corrispondenza trovata</Row>}
            </PageSection>
          </>
        )}
      </div>
    </PageSection>
  )
}

export declare interface Column {
  label: string
  width?: any
  style?: any
  hidden?: boolean
  isSortable?: boolean
  field: string
  render?: any
  options?: any
}
export declare interface Riga {
  settore?: string
  tipo_corso?: string
  anno_calc_cfp?: number
  nome_corso?: string
  cod_attivita?: string
  cfp_riconosciuti?: number
  cfp_validi?: number
  organizzatore?: string
  attestato?: any
  id_corso?: string
  evento?: any
  attestatoPartecipazione?: any
  attivitaFormativaExt?: any
  settoreNum?: number
  tipologia_evento?: number
  dataInizio?: string
  dataFine?: string
}
export declare interface Option {
  value: number
  label: string
}

export default CreditiFormativi
